import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretLeftIcon, InfoIcon, TrashIcon } from '@phosphor-icons/react';
import { CODE_ONLY_CATEGORIES, formatCurrency, splitCategoryEmoji } from '@budget/core';
import { Checkbox } from '../components/Checkbox';
import { Modal, ModalFooter } from '../components/Modal';
import { Select } from '../components/Select';
import { Tooltip } from '../components/Tooltip';
import { useCurrentUser } from '../auth/useCurrentUser';
import { useDashboard } from '../data/useDashboard';
import { useRules, type CategoryRule, type UpdateRuleInput } from '../data/useRules';

// Keep this in sync with the helper of the same name in TransactionsPage.
// Display-only: strip parenthetical clarifiers from sub-item names.
function displayCategory(name: string): string {
  return name.replace(/\s*\([^)]*\)/g, '').trim();
}

export function RulesPage() {
  const me = useCurrentUser();
  const rules = useRules();
  const dashboard = useDashboard();
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);

  // Group budget items by their parent for the rule edit modal's category
  // dropdown. Mirrors the structure used by the recategorize modal.
  const budgetGroups = useMemo(() => {
    const cats = dashboard.data?.budget.categories ?? [];
    return cats
      .map((c) => ({
        category: c.name,
        items: c.items.map((i) => i.name).filter((n) => n.length > 0),
      }))
      .filter((g) => g.items.length > 0);
  }, [dashboard.data]);

  // Bare parent name → full sheet name (with emoji prefix). Used to resolve
  // an orphan stored value like "Food & Beverages" into its canonical
  // "🍔 Food & Beverages" for the dropdown label.
  const categoryByBareName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of dashboard.data?.budget.categories ?? []) {
      map.set(splitCategoryEmoji(c.name).name, c.name);
    }
    return map;
  }, [dashboard.data]);

  const editingRule =
    rules.status === 'ready' && editingRuleId != null
      ? rules.rules.find((r) => r.id === editingRuleId) ?? null
      : null;

  const visibleRules = rules.status === 'ready' ? rules.rules : [];

  if (me.status === 'ready' && me.mode !== 'real') {
    return (
      <div className="page-container-narrow space-y-6">
        <header className="page-header">
          <div>
            <Link
              to="/settings"
              className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg mb-2"
            >
              <CaretLeftIcon size={14} weight="bold" /> Settings
            </Link>
            <h1 className="page-title">Categorization rules</h1>
            <p className="page-subtitle">Sign in with Google to manage rules.</p>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="page-container-narrow space-y-8">
      <header className="page-header">
        <div>
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg mb-2"
          >
            <CaretLeftIcon size={14} weight="bold" /> Settings
          </Link>
          <h1 className="page-title">Categorization rules</h1>
          <p className="page-subtitle">
            Auto-applied to matching transactions during sync.
          </p>
        </div>
      </header>

      {rules.status === 'loading' && (
        <div className="card-flush">
          <div className="settings-row">
            <div className="text-sm text-fg-secondary">Loading…</div>
          </div>
        </div>
      )}
      {rules.status === 'error' && (
        <div className="card-flush">
          <div className="settings-row">
            <div className="text-sm text-red">{rules.error}</div>
          </div>
        </div>
      )}
      {rules.status === 'ready' && (
        <>
          {visibleRules.length === 0 ? (
            <div className="card-flush">
              <div className="settings-row">
                <div className="text-sm text-fg-tertiary">No rules yet.</div>
              </div>
            </div>
          ) : (
            <div className="card-flush">
              {visibleRules.map((r) => (
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-center px-4 py-3 sm:px-5 border-b border-border last:border-b-0 cursor-pointer hover:bg-overlay transition-colors"
                  key={r.id}
                  onClick={() => setEditingRuleId(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setEditingRuleId(r.id);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="sm:hidden">
                      <div className="font-medium truncate">
                        {r.merchantPattern}
                        {r.amount != null && (
                          <span className="ml-2 text-xs text-fg-tertiary tabular font-normal">
                            {formatCurrency(r.amount)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-fg-secondary truncate mt-0.5">
                        {displayCategory(r.budgetCategory)}
                      </div>
                    </div>
                    <div className="hidden sm:block font-medium truncate">
                      {r.merchantPattern}
                      {r.amount != null && (
                        <span className="ml-2 text-xs text-fg-tertiary tabular font-normal">
                          {formatCurrency(r.amount)}
                        </span>
                      )}
                      <span className="text-fg-tertiary mx-2">→</span>
                      <span className="text-fg-secondary">
                        {displayCategory(r.budgetCategory)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void rules.remove(r.id).catch(() => undefined);
                    }}
                    aria-label="Delete rule"
                    className="btn btn-outline ml-4 shrink-0 hover:!bg-red-soft hover:!border-red hover:!text-red"
                  >
                    <TrashIcon size={16} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editingRule && rules.status === 'ready' && (
        <EditRuleModal
          rule={editingRule}
          budgetGroups={budgetGroups}
          categoryByBareName={categoryByBareName}
          onClose={() => setEditingRuleId(null)}
          onSave={(input) => rules.update(editingRule.id, input)}
        />
      )}
    </div>
  );
}

function EditRuleModal({
  rule,
  budgetGroups,
  categoryByBareName,
  onClose,
  onSave,
}: {
  rule: CategoryRule;
  budgetGroups: { category: string; items: string[] }[];
  categoryByBareName: Map<string, string>;
  onClose: () => void;
  onSave: (input: UpdateRuleInput) => Promise<{ backfilled: number }>;
}) {
  const [pattern, setPattern] = useState(rule.merchantPattern);
  const [category, setCategory] = useState(rule.budgetCategory);
  // Amount restriction: when on, the input holds a positive number; when
  // off, the rule matches any amount (server stores NULL). String state so
  // the input behaves like a normal numeric field (user can type "4." mid-
  // edit without it getting clamped).
  const [restrictAmount, setRestrictAmount] = useState<boolean>(rule.amount != null);
  const [amountInput, setAmountInput] = useState<string>(
    rule.amount != null ? rule.amount.toFixed(2) : '',
  );
  const [backfill, setBackfill] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ backfilled: number } | null>(null);

  // If the stored category isn't in the dropdown's known options, surface
  // it as a synthetic option so the select reflects current state instead
  // of falling back to "Uncategorized".
  const valueInOptions =
    (CODE_ONLY_CATEGORIES as readonly string[]).includes(category) ||
    budgetGroups.some((g) => g.items.includes(category));
  const orphanLabel =
    category !== '' && !valueInOptions
      ? displayCategory(categoryByBareName.get(category) ?? category)
      : null;

  const parsedAmount = Number(amountInput);
  const nextAmount: number | null = restrictAmount
    ? Number.isFinite(parsedAmount) && parsedAmount > 0
      ? Math.round(parsedAmount * 100) / 100
      : NaN
    : null;
  const amountInvalid = restrictAmount && Number.isNaN(nextAmount);
  const amountChanged =
    (rule.amount == null && nextAmount != null) ||
    (rule.amount != null && nextAmount == null) ||
    (rule.amount != null && nextAmount != null && rule.amount !== nextAmount);

  const dirty =
    pattern !== rule.merchantPattern ||
    category !== rule.budgetCategory ||
    amountChanged;
  const canSave =
    dirty &&
    pattern.trim().length > 0 &&
    category.trim().length > 0 &&
    !amountInvalid;

  const save = async () => {
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const out = await onSave({
        merchantPattern: pattern,
        budgetCategory: category,
        // Always send amount so a toggle to "off" actually clears the
        // server-side constraint. null when off, a positive number when on.
        amount: nextAmount,
        backfill,
      });
      if (backfill) {
        // Hold the modal open so the user sees how many rows were updated.
        setResult({ backfilled: out.backfilled });
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-6 pb-2 flex items-center justify-between gap-3">
        <div className="text-lg font-semibold">Edit rule</div>
        <span className="badge bg-overlay text-fg-tertiary">
          {rule.scope === 'household' ? 'Household rule' : 'Personal rule'}
        </span>
      </div>

      <div className="px-6 pb-6 pt-2 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary inline-flex items-center gap-1.5">
            Merchant Keyword(s)
            <Tooltip
              wide
              content="Substring match against the transaction's merchant name (lowercased, whitespace collapsed). 'amazon' matches 'Amazon', 'Amazon Marketplace', 'Buy at amazon now', etc. Longer rules win as a tiebreaker — 'starbucks coffee' beats 'starbucks' if both match."
            >
              <InfoIcon
                size={12}
                weight="regular"
                className="text-fg-tertiary cursor-help"
                tabIndex={0}
                aria-label="About merchant keyword matching"
              />
            </Tooltip>
          </label>
          <input
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-subtle outline-none focus:border-border-strong text-sm"
            placeholder="e.g. amazon"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary">
            Category
          </label>
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Uncategorized</option>
            {CODE_ONLY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            {orphanLabel && <option value={category}>{orphanLabel}</option>}
            {budgetGroups.map((g) => (
              <optgroup key={g.category} label={displayCategory(g.category)}>
                {g.items.map((i) => (
                  <option key={i} value={i}>
                    {displayCategory(i)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Checkbox
            checked={restrictAmount}
            onChange={setRestrictAmount}
            label="Only include transactions for a specific amount"
          />
          {restrictAmount && (
            <div className="pl-6 flex items-center gap-2">
              <span className="text-fg-tertiary text-sm">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                className="px-3 py-2 rounded-lg border border-border bg-subtle outline-none focus:border-border-strong text-sm tabular w-32"
                placeholder="0.00"
              />
              {amountInvalid && (
                <span className="text-xs text-red">enter a positive amount</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            checked={backfill}
            onChange={setBackfill}
            label="Also recategorize past matching transactions"
          />
          <Tooltip
            wide
            content="Manually-categorized transactions are never overwritten."
          >
            <InfoIcon
              size={12}
              weight="regular"
              className="text-fg-tertiary cursor-help"
              tabIndex={0}
              aria-label="About backfilling past transactions"
            />
          </Tooltip>
        </div>

        {result && (
          <div className="text-sm text-fg-secondary">
            Updated {result.backfilled} past transaction
            {result.backfilled === 1 ? '' : 's'}.
          </div>
        )}
        {error && <div className="text-sm text-red break-words">{error}</div>}
      </div>

      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="btn btn-outline"
        >
          {result ? 'Done' : 'Cancel'}
        </button>
        {!result && (
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || !canSave}
            className="btn btn-primary"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}

